import React from 'react';
import {
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  CameraAlt,
  Receipt,
  AccountBalance,
} from '@mui/icons-material';
import { useNavigate } from '@tanstack/react-router';

/** Actions available in the quick-add floating action button. */
const ACTIONS = [
  {
    icon: <CameraAlt />,
    name: 'Add Snapshot',
    path: '/snapshots',
  },
  {
    icon: <Receipt />,
    name: 'Add Transaction',
    path: '/transactions',
  },
  {
    icon: <AccountBalance />,
    name: 'Add Account',
    path: '/accounts',
  },
] as const;

/**
 * Floating action button with speed dial for quick-add actions on mobile.
 *
 * Displays a SpeedDial component in the bottom-right corner that provides
 * quick navigation to add snapshots, transactions, or accounts.
 * Only visible on mobile devices (below 'md' breakpoint).
 */
export const QuickAddFAB: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  if (!isMobile) {
    return null;
  }

  /**
   * Handles navigation when a speed dial action is clicked.
   *
   * Args:
   *   path: The route path to navigate to.
   */
  const handleAction = (path: string) => {
    navigate({ to: path });
  };

  return (
    <SpeedDial
      ariaLabel="Quick add actions"
      sx={{
        position: 'fixed',
        bottom: 80,
        right: 16,
        zIndex: 1050,
      }}
      icon={<SpeedDialIcon />}
    >
      {ACTIONS.map((action) => (
        <SpeedDialAction
          key={action.name}
          icon={action.icon}
          tooltipTitle={action.name}
          onClick={() => handleAction(action.path)}
        />
      ))}
    </SpeedDial>
  );
};
